import type { Form } from 'factorial-form'
import each from 'lodash/each'
import findIndex from 'lodash/findIndex'
import type { IObservableArray } from 'mobx'
import { computed, makeObservable, observable } from 'mobx'

import type { FieldType } from 'modules/reports/components/Editor/Field/types'
import { UI_GROUPS } from 'modules/reports/lib/Constants'
import fields from 'modules/reports/store/collections/fields'
import type Field from 'modules/reports/store/models/Field'

class Base<T extends Form> {
  forms!: IObservableArray<T>
  FormClass: any

  constructor() {
    makeObservable(this, {
      forms: observable,
      selectedFieldTypes: computed,
    })
  }

  findForm(index: number): T | null | undefined {
    if (!this.forms || this.forms.length - 1 < index) return null

    return this.forms[index]
  }

  get selectedFieldTypes() {
    if (!this.forms) {
      return []
    }

    return this.forms.flatMap((form) => {
      const field = fields.find({
        path: form.get('path').value,
      })

      if (field === undefined) return []
      return [{ form, field }]
    })
  }

  selectedFieldTypesByUiGroup(uiGroup?: string) {
    if (!this.forms) {
      return []
    }

    if (!uiGroup) return this.selectedFieldTypes

    return this.forms
      .filter((form) => form.get('ui_group').value === uiGroup)
      .flatMap((form) => {
        const field = fields.find({
          path: form.get('path').value,
        })

        if (field === undefined) return []
        return [{ form, field }]
      })
  }

  excludedFieldPathsFromUiGroup(uiGroup?: string) {
    if (!uiGroup || !this.forms) return []

    const oppositeUiGroup = uiGroup === UI_GROUPS.abscissa ? UI_GROUPS.ordinate : UI_GROUPS.abscissa

    return this.forms
      .filter((form: Form) => form.get('ui_group').value === oppositeUiGroup)
      .flatMap((form: Form) => {
        const field = fields.find({
          path: form.get('path').value,
        })

        if (!field) return []

        return [field.attributes.path as string]
      })
  }

  removeField(fieldType: FieldType) {
    this.forms?.replace(this.forms.filter((item: any) => item !== fieldType.form))
  }

  removeFormsFromIndexToEnd(index: number) {
    this.forms?.replace(this.forms.filter((_item, itemIndex) => itemIndex < index))
  }

  resetForms() {
    this.forms?.replace([])
  }

  addFields(fields: Field[]) {
    each(fields, (field: Field) => {
      const fieldIndex = findIndex(
        this.forms,
        (form: Form) => form.get('path').value === field.attributes.path
      )

      if (this.forms) {
        const index = fieldIndex >= 0 ? fieldIndex : this.forms.length
        this.updateOrCreate(index, {
          path: field.attributes.path,
        })
      }
    })
  }

  updateOrCreate(index: number, values: Record<string, any>) {
    const form = this.findForm(index)
    if (form) {
      form.setValues(values)
    } else if (this.forms) {
      this.forms[index] = new this.FormClass(values)
    }
  }
}

export default Base
